// The wizard's step model (Module 02 §3.1, §4 S1) as PURE data + pure functions,
// so the rail, the resume CTA and the go-live gate are unit-testable without a
// DOM or a network.
//
// Five steps, fixed by the contract (`onboarding_step_states.step_no BETWEEN 1
// AND 5` — step 5 APPENDED 2026-08-23, D-214). Steps are resumable and
// INDEPENDENTLY editable — step 3 may be entered before step 1 is complete —
// so nothing here "locks" a later step.

import type { components } from "@assessly/api-client";

type S = components["schemas"];

export type SessionStatus = S["OnboardingSession"]["status"];
export type StepStatus = S["OnboardingStepState"]["status"];

/** Step numbers are 1–5 (§2.2 CHECK; 5 appended by D-214). */
export type StepNo = 1 | 2 | 3 | 4 | 5;

/**
 * The onboarding routes that EXIST (router.tsx). Kept as a literal union so a
 * typo cannot slip into the rail and so `<Link to={…}>` stays type-checked
 * against the router's route tree.
 */
export type OnboardingRoutePath =
  | "/onboarding"
  | "/onboarding/vertical"
  | "/onboarding/catalogue"
  | "/onboarding/plant"
  | "/onboarding/opening"
  | "/onboarding/quality"
  | "/onboarding/checklist";

export interface WizardStepDef {
  readonly stepNo: StepNo;
  /** i18n key for the step title (never a literal — the i18n rule). */
  readonly titleKey: string;
  /** i18n key for the one-line "what this step is for" copy. */
  readonly blurbKey: string;
  /**
   * Route the step opens. `null` would mean the step can never be COMPLETED
   * from the UI, which (step 4 being one of the four `ready_for_review`
   * requirements) would make go-live unreachable — so a step without a route is
   * a broken product, not a placeholder. All four are routed.
   */
  readonly to: OnboardingRoutePath | null;
}

/** The five wizard steps in order (§3.1–§3.5b). */
export const WIZARD_STEPS: readonly WizardStepDef[] = [
  {
    stepNo: 1,
    titleKey: "onboarding.steps.vertical.title",
    blurbKey: "onboarding.steps.vertical.blurb",
    to: "/onboarding/vertical",
  },
  {
    stepNo: 2,
    titleKey: "onboarding.steps.catalogue.title",
    blurbKey: "onboarding.steps.catalogue.blurb",
    to: "/onboarding/catalogue",
  },
  {
    stepNo: 3,
    titleKey: "onboarding.steps.plant.title",
    blurbKey: "onboarding.steps.plant.blurb",
    to: "/onboarding/plant",
  },
  {
    stepNo: 4,
    titleKey: "onboarding.steps.opening.title",
    blurbKey: "onboarding.steps.opening.blurb",
    to: "/onboarding/opening",
  },
  {
    stepNo: 5,
    titleKey: "onboarding.steps.quality.title",
    blurbKey: "onboarding.steps.quality.blurb",
    to: "/onboarding/quality",
  },
];

export const STEP_COUNT = WIZARD_STEPS.length;

/** The go-live completeness gate (§3.6). The SERVER re-checks it under the
 * wizard lock — this constant only drives the disabled state + the tooltip. */
export const GO_LIVE_SCORE_THRESHOLD = 85;

export const CHECKLIST_ROUTE = "/onboarding/checklist" as const;

/** The step state for `stepNo`, or `not_started` when the server has no row. */
export function stepStatusOf(
  steps: readonly S["OnboardingStepState"][],
  stepNo: number,
): StepStatus {
  return steps.find((s) => s.step_no === stepNo)?.status ?? "not_started";
}

export function stepStateOf(
  steps: readonly S["OnboardingStepState"][],
  stepNo: number,
): S["OnboardingStepState"] | undefined {
  return steps.find((s) => s.step_no === stepNo);
}

/** Every step completed ⇒ the session is (or is about to be) ready_for_review. */
export function allStepsComplete(steps: readonly S["OnboardingStepState"][]): boolean {
  return WIZARD_STEPS.every((d) => stepStatusOf(steps, d.stepNo) === "completed");
}

/** Step numbers still incomplete, ascending — mirrors the go-live 422's
 * `incomplete_steps[]` so the UI can show the same list before it is refused. */
export function incompleteSteps(steps: readonly S["OnboardingStepState"][]): StepNo[] {
  return WIZARD_STEPS.filter((d) => stepStatusOf(steps, d.stepNo) !== "completed").map(
    (d) => d.stepNo,
  );
}

/** Where "Resume where I left off" goes (S1). */
export type ResumeTarget =
  /** Open this step's screen. */
  | { readonly kind: "step"; readonly stepNo: StepNo; readonly to: OnboardingRoutePath }
  /** The next unfinished step has no screen yet (step 4 today). */
  | { readonly kind: "step_unavailable"; readonly stepNo: StepNo }
  /** Nothing left to do in the steps — go read the checklist. */
  | { readonly kind: "checklist"; readonly to: typeof CHECKLIST_ROUTE };

/**
 * Resume rule (§3.1 "closing the browser loses nothing"):
 * 1. the session's `current_step`, when that step is not yet completed — this is
 *    literally where the admin last worked;
 * 2. otherwise the LOWEST incomplete step;
 * 3. otherwise (all four complete, or the session is live) the checklist.
 * A step whose screen is not built resolves to `step_unavailable` rather than a
 * dead link.
 */
export function resumeTarget(
  session: Pick<S["OnboardingSession"], "current_step" | "status">,
  steps: readonly S["OnboardingStepState"][],
): ResumeTarget {
  if (session.status === "live") return { kind: "checklist", to: CHECKLIST_ROUTE };
  const pending = incompleteSteps(steps);
  if (pending.length === 0) return { kind: "checklist", to: CHECKLIST_ROUTE };
  const current = pending.includes(session.current_step as StepNo)
    ? (session.current_step as StepNo)
    : pending[0];
  const def = WIZARD_STEPS.find((d) => d.stepNo === current);
  if (!def) return { kind: "checklist", to: CHECKLIST_ROUTE };
  return def.to === null
    ? { kind: "step_unavailable", stepNo: def.stepNo }
    : { kind: "step", stepNo: def.stepNo, to: def.to };
}

/** Why go-live is not offered yet (S1 tooltip). Empty ⇒ the button is live. */
export interface GoLiveBlockers {
  /** Steps still incomplete (§3.1 — 422 PRECONDITIONS_UNMET). */
  readonly incompleteSteps: readonly StepNo[];
  /** Blocking checklist item keys not passed/waived (422 BLOCKING_ITEMS_UNMET). */
  readonly blockingUnmet: readonly string[];
  /** Present when score < 85 (422 SCORE_BELOW_THRESHOLD). */
  readonly scoreGap: number | null;
  /** The caller cannot go live (§7: group_admin only). */
  readonly notPermitted: boolean;
  /** The tenant is already live — the wizard is frozen history (§3.1). */
  readonly alreadyLive: boolean;
  readonly ok: boolean;
}

/**
 * The CLIENT MIRROR of the §3.6 go-live gate. The server re-evaluates all of it
 * inside the wizard advisory lock and is the only decision that counts; this
 * exists so the button is honest about WHY it is disabled instead of failing on
 * click.
 */
export function goLiveBlockers(input: {
  session: Pick<S["OnboardingSession"], "current_step" | "status">;
  steps: readonly S["OnboardingStepState"][];
  summary: S["OnboardingChecklistSummary"];
  canGoLive: boolean;
}): GoLiveBlockers {
  const pending = incompleteSteps(input.steps);
  const gap =
    input.summary.score < GO_LIVE_SCORE_THRESHOLD
      ? GO_LIVE_SCORE_THRESHOLD - input.summary.score
      : null;
  const alreadyLive = input.session.status === "live";
  return {
    incompleteSteps: pending,
    blockingUnmet: input.summary.blocking_unmet,
    scoreGap: gap,
    notPermitted: !input.canGoLive,
    alreadyLive,
    ok:
      !alreadyLive &&
      input.canGoLive &&
      pending.length === 0 &&
      input.summary.blocking_unmet.length === 0 &&
      gap === null,
  };
}

/** True once the tenant is live: every wizard mutation answers 409 ALREADY_LIVE,
 * so the whole area renders read-only (§3.1, S8 "After go-live"). */
export function isWizardFrozen(session: Pick<S["OnboardingSession"], "status">): boolean {
  return session.status === "live";
}

/**
 * Screens a wizard STEP opens as part of its own work, as opposed to
 * destinations that leave the step behind — PER STEP, not globally.
 *
 * This is what the unsaved-changes guard (`SaveContinueBar`) is scoped by
 * (spec 02 §3.8). The guard exists to stop someone losing a draft on the way
 * OUT of a step; asking "leave without saving?" when the step is merely opening
 * one of its own screens is both untrue and, in one case, actively harmful.
 *
 * The exemption is keyed by (step, destination), because ownership is a
 * property of the STEP, not of the destination alone: the import detail screen
 * (§4 S4) belongs to steps 2 and 4, which open it from their import lanes —
 * steps 1 and 3 own nothing and cannot legitimately reach it. A destination-only
 * exemption silently waved through a real data-loss sequence: step 2 → import
 * detail → rail to step 3 → long unsaved plant-map edit → browser BACK (the
 * previous history entry IS the import detail — blockers run on pops too), and
 * the unsaved tree was gone without a word.
 *
 * Why the S4 exemption exists for steps 2/4:
 *   * S4's own back link returns to the step it was opened from
 *     (`stepRouteForKind`), so the step is being resumed, not abandoned (the
 *     step's own unsaved SCRATCH — a tab, a typed as_of_date — does not survive
 *     the round trip unless saved; the uploaded file and job row always do);
 *   * the post-upload hand-off to it happens AFTER the bytes are in S3 and the
 *     import job row exists. Blocking that navigation strands the user on step 2
 *     with an uploaded file they cannot reach — which is how S4 became
 *     unreachable from every in-app entry path in the first place.
 *
 * Matched by pathname prefix rather than by route id so it holds for the
 * post-upload redirect, the jobs-table Open button and the mobile card alike —
 * all three navigate to the same route. A new step-owned screen is added to the
 * owning step's list here, with its reason, or it will be challenged.
 */
const STEP_OWNED_PATH_PREFIXES: Readonly<Record<StepNo, readonly string[]>> = {
  1: [],
  2: ["/onboarding/imports/"],
  3: [],
  4: ["/onboarding/imports/"],
  // Step 5 (D-214) opens nothing of its own: band edits and deactivations
  // commit immediately through module 07's doors, so there is no draft to
  // protect on the way out and no owned screen to exempt.
  5: [],
};

export function staysWithinStep(stepNo: StepNo, nextPathname: string): boolean {
  return STEP_OWNED_PATH_PREFIXES[stepNo].some((prefix) => nextPathname.startsWith(prefix));
}

/**
 * A stable string for a step payload, with object keys sorted at every depth.
 *
 * The guard's "has this been edited?" baseline is the payload the screen LOADED
 * the step with, and that value has been through Postgres, where the column is
 * `jsonb` (migration 0006) — a type that does not preserve key insertion order.
 * So the screen's object literal and the very same payload after a server round
 * trip differ as STRINGS while being equal as VALUES, and a plain
 * `JSON.stringify` compare would report an untouched step as edited.
 *
 * Array order is preserved: in a plant map the order of facilities is content,
 * not formatting.
 */
export function canonicalPayload(value: unknown): string {
  return JSON.stringify(sortKeysDeep(value));
}

function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value === null || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => [k, sortKeysDeep(v)]),
  );
}
